import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Tabs,
  Tab,
  MenuItem,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Select,
  InputLabel,
  FormControl,
  Radio,
  Grid,
  styled,
  FormHelperText,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { ROUTE_COMPANY_JOBS_DASHBOARD, ROUTE_JOBS_DASHBOARD, ROUTE_VIEW_JOB } from "../../constants/routes";
import MultiSelect from "../common/MultiSelect";
import { getKeyValuePairs } from "../../redux/services/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_CITIES,
  SET_COURSE_NAMES,
  SET_FUNCTIONS,
  SET_JOB_ROLES,
  SET_PERKS,
  SET_SKILLS,
  SET_TOOLS_KNOWN,
} from "../../redux/actions/actionTypes";
import { getMasterData } from "../../redux/actions/appActions";
import { CssSelectTextField } from "../common/CssTextField";
import {
  createJob,
  editJob,
  getFunctionPersonality,
  getJobDetailsById,
  getPersonalityMaster,
} from "../../redux/actions/b2bActions";
import { ArrowForward } from "@mui/icons-material";
import { JOB_TYPES, WORK_MODES } from "../../constants/AppVarConstants";
import RecommendJobModal from "./RecommendJobModal";

const JobType = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
}));

const labelStyles = {
  transform: "none",
  position: "relative",
  fontSize: "14px",
  color: "#1F2235",
  fontWeight: "500",
  zIndex: 0,
};

const labelStylestextfield = {
  transform: "none",
  position: "relative",
  top: "10px",
  fontSize: "14px",
  color: "#1F2235",
  fontWeight: "500",
};

const labelStyleJobDescription = {
  transform: "none",
  position: "relative",
  top: "10px",
  fontSize: "14px",
  color: "#1F2235",
  fontWeight: "700",
};

const TextInputField = ({ name, label, control, placeholder, isTextArea = false, disabled = false }: any) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      width: "100%",
    }}
  >
    <InputLabel shrink sx={labelStylestextfield}>
      {label}
    </InputLabel>
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <TextField
            {...field}
            fullWidth
            placeholder={placeholder}
            error={!!error}
            disabled={disabled}
            multiline={isTextArea}
            minRows={isTextArea ? 3 : 1}
            sx={{
              borderRadius: isTextArea ? "10px" : "95px",
              color: "#777777",
              boxShadow: "inset 0 0 11px 2px #dfd9d9",
              padding: "0 5px",
            }}
            inputProps={{
              style: {
                padding: "12.5px 14px",
              },
            }}
          />
          <FormHelperText sx={{ color: "#d32f2f", mt: -2, ml: "14px" }}>{error ? error.message : " "}</FormHelperText>
        </>
      )}
    />
  </Box>
);

type FormValues = Yup.InferType<any>;

const schema = [
  Yup.object().shape({
    type: Yup.string(),
    jobRole: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required("Please select a job role"),
    function: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required("Please select a function"),
    jobType: Yup.array().min(1, "Please select job type"),
    workMode: Yup.array().min(1, "Please select work mode"),
    qualification: Yup.array().min(1, "Please select qualification"),
    location: Yup.array().min(1, "Please select location"),
    minSal: Yup.number().typeError("Min salary is required").min(0, "Min salary must be greater than or equal to 0"),
    maxSal: Yup.number()
      .typeError("Max salary is required")
      .min(Yup.ref("minSal"), "Max salary must be greater than min salary"),
    minExp: Yup.number()
      .typeError("Min experience is required")
      .min(0, "Min experience must be greater than or equal to 0"),
    maxExp: Yup.number()
      .typeError("Max experience is required")
      .min(Yup.ref("minExp"), "Max experience must be greater than min experience"),
    skills: Yup.array().min(1, "Please select skills"),
    tools: Yup.array(),
    relocate: Yup.boolean(),
    minExpUnit: Yup.string().required("Please select a unit for minimum experience"),
    maxExpUnit: Yup.string().required("Please select a unit for maximum experience"),
  }),
  Yup.object().shape({
    title: Yup.string().required("Job title is required"),
    keyResultAreas: Yup.string().required("KRA's are required"),
    hiringProcess: Yup.string().required("Hiring process is required"),
    numberOfPosition: Yup.string().required("No. of positions is required"),
    perks: Yup.array().min(1, "Please select perks"),
  }),
  Yup.object().shape({
    personalityTypes: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required("Please select personality type"),
  }),
];

const jobTypes = ["Internship", "Job"];

const CreateJob = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { companyId } = useParams<{ companyId: string }>();
  const [viewAll, setViewAll] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [recommendJobModalOpen, setRecommendJobModalOpen] = useState(false);
  const { toolsKnown, skills, perks } = useSelector((state: any) => state.app);
  const { personalities, clientJobDetails, functionPersonalities } = useSelector((state: any) => state.b2b);
  const { jobRoles, cities, functions, courseNames } = useSelector((state: any) => state.app);
  const roles = localStorage.getItem("roles");
  const isSeeviAdmin = roles?.includes('seevi_admin');

  const {
    jobCategory,
    jobRole,
    functions: editFunctions,
    location: editLocations,
    minExperience,
    maxExperience,
    preferredQualification,
    minCtc,
    maxCtc,
    tools,
    skills: editSkills,
    relocate,
    title,
    workMode,
    perks: editPerks,
    jobType,
    hiringProcess,
    numberOfPosition,
    keyResultAreas,
  } = clientJobDetails;

  const jobIdParam = searchParams?.get("jobId");
  const hasEditQuery = location?.search?.includes("edit");
  const isReadOnlyView = location?.pathname === ROUTE_VIEW_JOB;
  const isEditMode = Boolean(jobIdParam && hasEditQuery && !isReadOnlyView);
  const shouldPrefill = Boolean(jobIdParam);
  const blankValues = {
    type: "Internship",
    jobRole: null,
    function: null,
    jobType: [],
    workMode: [],
    qualification: [],
    location: [],
    minSal: "",
    maxSal: "",
    minExp: "",
    maxExp: "",
    minExpUnit: "",
    maxExpUnit: "",
    skills: [],
    tools: [],
    relocate: false,
    title: "",
    keyResultAreas: "",
    hiringProcess: "",
    numberOfPosition: "",
    perks: [],
    personalityTypes: [],
  };
  
  const defaultValues = shouldPrefill ? {
    type: jobCategory || "Internship",
    jobRole: jobRole?.length ? { label: jobRole?.[0], value: jobRole?.[0] } : null,
    function: editFunctions?.length ? { label: editFunctions?.[0], value: editFunctions?.[0] } : null,
    jobType: jobType?.length ? getKeyValuePairs(jobType) : [],
    workMode: workMode?.length ? getKeyValuePairs(workMode) : [],
    qualification: preferredQualification?.length ? getKeyValuePairs(preferredQualification) : [],
    location: editLocations?.length ? getKeyValuePairs(editLocations) : [],
    minSal: minCtc || "",
    maxSal: maxCtc || "",
    minExp: minExperience?.length ? minExperience?.split(" ")?.[0] : "",
    maxExp: maxExperience?.length ? maxExperience?.split(" ")?.[0] : "",
    minExpUnit: minExperience?.length ? minExperience?.split(" ")?.[1] : "",
    maxExpUnit: maxExperience?.length ? maxExperience?.split(" ")?.[1] : "",
    skills: editSkills?.length ? getKeyValuePairs(editSkills) : [],
    tools: tools?.length ? getKeyValuePairs(tools) : [],
    relocate: relocate || false,
    title: title || "",
    keyResultAreas: keyResultAreas || "",
    hiringProcess: hiringProcess || "",
    numberOfPosition: numberOfPosition || "",
    perks: editPerks?.length ? getKeyValuePairs(editPerks) : [],
    personalityTypes: editFunctions?.length ? getKeyValuePairs(functions) : [],
  } : blankValues;

  const headerTitle = isReadOnlyView ? "View" : isEditMode ? "Edit" : "Create";

  const { handleSubmit, control, watch, trigger, reset, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema[activeTab]),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    dispatch(getMasterData("FUNCTIONS", SET_FUNCTIONS));
    dispatch(getMasterData("TOOLS_KNOWN", SET_TOOLS_KNOWN));
    dispatch(getMasterData("PERKS", SET_PERKS));
    dispatch(getMasterData("CITY", SET_CITIES));
    dispatch(getMasterData("COURSE_NAME", SET_COURSE_NAMES));
    dispatch(getPersonalityMaster());
    if (shouldPrefill && jobIdParam) {
      dispatch(getJobDetailsById(jobIdParam));
    } else {
      reset({ ...blankValues });
    }
    return () => {
      reset();
    };
  }, [shouldPrefill, jobIdParam, location?.pathname]);

  useEffect(() => {
    if (shouldPrefill) {
      reset(defaultValues);
    } else {
      reset({ ...blankValues });
    }
  }, [clientJobDetails, shouldPrefill]);

  const handleTabChange1 = async (event: any, newValue: any) => {
    if (isReadOnlyView) {
      setActiveTab(newValue);
      return;
    }
    const isValid = await trigger();

    if (isValid) {
      setActiveTab(newValue);
    }
  };

  const watchFunction = watch("function");

  const handleNext = async () => {
    if (isReadOnlyView) return;
    const isValid = await trigger(); // Validate only current tab
    if (isValid) setActiveTab((prev) => prev + 1);
  };

  const handleBack = () => {
    if (isReadOnlyView) return;
    setActiveTab((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    if (watchFunction?.value) {
      setValue("personalityTypes", { label: watchFunction.value, value: watchFunction.value });
      dispatch(getMasterData("JOB_ROLES", SET_JOB_ROLES, `parentId=${watchFunction?.value}`));
      dispatch(getMasterData("SKILL", SET_SKILLS, `parentId=${watchFunction?.value}`));
      dispatch(getFunctionPersonality(watchFunction?.value));
    }
  }, [watchFunction]);

  const filteredPersonalities = personalities?.filter(
    (el: any) => !functionPersonalities?.some((fp: any) => fp.displayPersonalityType === el.displayPersonalityType)
  );

  const onSubmit = (data: FormValues) => {
    if (isReadOnlyView) return;
    const payload: any = {
      jobCategory: data?.type,
      jobRole: [data?.jobRole?.value],
      jobType: data?.jobType?.map?.((el: any) => el?.value),
      workMode: data?.workMode?.map?.((el: any) => el?.value),
      location: data?.location?.map?.((el: any) => el?.value),
      minCtc: data?.minSal,
      maxCtc: data?.maxSal,
      skills: data?.skills?.map((el: { value: string }) => el?.value),
      tools: data?.tools?.map((el: { value: string }) => el?.value),
      relocate: data?.relocate,
      functions: [data?.function?.value],
      preferredQualification: data?.qualification?.map((el: { value: string }) => el?.value),
      minExperience: `${data?.minExp} ${data?.minExpUnit}`,
      maxExperience: `${data?.maxExp} ${data?.maxExpUnit}`,
      title: data?.title,
      keyResultAreas: data?.keyResultAreas,
      hiringProcess: data?.hiringProcess,
      numberOfPosition: data?.numberOfPosition,
      perks: data?.perks?.map((el: { value: string }) => el?.value),
      personalityTypes: functionPersonalities?.map((el: any) => el?.personalityType),
    };
    
    if (location?.search?.includes("edit")) {
      payload.jobId = searchParams.get("jobId");
    //  dispatch(editJob(payload, () => navigate(ROUTE_JOBS_DASHBOARD)));
      dispatch(editJob(payload, () => navigate(-1)));
  } else {
      if (companyId) {
      payload.companyId = companyId;
      }
   // dispatch(createJob(payload, () => navigate(companyId ? ROUTE_COMPANY_JOBS_DASHBOARD : ROUTE_JOBS_DASHBOARD)));
    dispatch(createJob(payload, () => navigate(-1)));
   //   dispatch(createJob(payload, () => navigate(companyId ? ROUTE_COMPANY_JOBS_DASHBOARD : ROUTE_JOBS_DASHBOARD)));
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff", // Page background color
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >

      <Box sx={{ display: "flex", flexDirection: "column", paddingLeft: "100px", paddingRight: "100px", alignItems: "left", width: "100%", position: "relative", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: "20px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              onClick={() => navigate(-1)}
              style={{ display: "flex", alignItems: "center", gap: 6, textDecoration: "none", marginTop: "20px", cursor: "pointer" }}
            >
              <img src="/images/client-website/back-arrow.png" width="5" ></img>
              <span>Back To Dashboard</span>
            </Typography>
            <Typography style={{ fontSize: "18px", fontWeight: "600", color: "#1F2235", marginTop: "10px" }}>
              {headerTitle} Job Post
            </Typography>
            {isReadOnlyView && (
              <Typography sx={{ fontSize: "14px", color: "#666", mt: 1 }}>
                This is a read-only preview of the job post. Editing is disabled.
              </Typography>
            )}
          </Box>
          {isSeeviAdmin && isEditMode && jobIdParam && (
            <Box sx={{ flexShrink: 0, marginTop: "20px" }}>
              <Button
                variant="contained"
                onClick={() => setRecommendJobModalOpen(true)}
                sx={{
                  backgroundColor: "#FF8960",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    backgroundColor: "#ff7848",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Recommend Job
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: "90%", margin: "20px", border: "1px solid #756de9", borderRadius: "10px", padding: "20px" }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange1}
          TabIndicatorProps={{
            style: { backgroundColor: "#756de9", height: "4px" }, // Adjust this as needed
          }}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              fontSize: "14px",
              fontWeight: "500",
              color: "#000",
            },
            "& .Mui-selected": {
              color: "#756de9", // Change color of the selected tab text
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#756de9 !important", // Ensure the indicator is visible
            },
          }}
        >
          <Tab label="JOB ROLE" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
          <Tab label="JOB DESCRIPTION" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
          <Tab label="PERSONALITY" sx={{ fontSize: "14px", weight: "500", color: "#000" }} />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ padding: "20px", width: "60%" }}>
            {/* Position Type */}
            <Controller
              name="type"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Box sx={{ display: "flex", gap: 5, mt: 4 }}>
                  {jobTypes.map((jobType) => (
                    <Grid item xs={4} key={jobType}>
                      <FormControlLabel
                        control={
                          <Radio
                            value={jobType}
                            onChange={(e) => onChange(e.target.value)}
                            checked={value === jobType}
                            disabled={isReadOnlyView}
                          />
                        }
                        label={
                          <JobType>
                            <Typography>{jobType}</Typography>
                          </JobType>
                        }
                      />
                    </Grid>
                  ))}
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </Box>
              )}
            />

            {/* Function Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="function-label" shrink={true} sx={labelStyles}>
                Function
              </InputLabel>
              <MultiSelect multiple={false} name="function" control={control} options={getKeyValuePairs(functions)} disabled={isReadOnlyView} />
            </FormControl>

            {/* Job Role Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="job-role-label" shrink={true} sx={labelStyles}>
                Job Role
              </InputLabel>
              <MultiSelect
                multiple={false}
                disabled={isReadOnlyView || !watchFunction?.label}
                name="jobRole"
                control={control}
                options={getKeyValuePairs(jobRoles)}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="job-role-label" shrink={true} sx={labelStyles}>
                Location
              </InputLabel>
              <MultiSelect
                name="location"
                control={control}
                options={getKeyValuePairs(cities)}
                freesolo
                disabled={isReadOnlyView}
              />
            </FormControl>

            {/* Relocate Toggle */}
            <Box
              sx={{
                marginTop: "16px",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#333",
                }}
              >
                Open to candidates who are willing to relocate to this location.
              </Typography>
              <Controller
                name="relocate"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Box
                    sx={{
                      display: "flex",
                      backgroundColor: "#F5F5F5",
                      borderRadius: "25px",
                      padding: "4px",
                      border: "1px solid #E0E0E0",
                      width: "fit-content",
                    }}
                  >
                    <Button
                      onClick={() => onChange(true)}
                      disabled={isReadOnlyView}
                      sx={{
                        borderRadius: "20px",
                        padding: "8px 24px",
                        fontSize: "14px",
                        fontWeight: "500",
                        textTransform: "none",
                        backgroundColor: value ? "#756DE9" : "transparent",
                        color: value ? "#FFFFFF" : "#666666",
                        minWidth: "80px",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: value ? "#5a4dbf" : "#E8E8F0",
                          color: value ? "#FFFFFF" : "#333333",
                        },
                        boxShadow: value ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => onChange(false)}
                      disabled={isReadOnlyView}
                      sx={{
                        borderRadius: "20px",
                        padding: "8px 24px",
                        fontSize: "14px",
                        fontWeight: "500",
                        textTransform: "none",
                        backgroundColor: !value ? "#756DE9" : "transparent",
                        color: !value ? "#FFFFFF" : "#666666",
                        minWidth: "80px",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: !value ? "#5a4dbf" : "#E8E8F0",
                          color: !value ? "#FFFFFF" : "#333333",
                        },
                        boxShadow: !value ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
                      }}
                    >
                      No
                    </Button>
                  </Box>
                )}
              />
            </Box>

            {/* Work Mode Dropdown */}
            <Grid sx={{ display: "flex", flexDirection: "row", gap: "20px" }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="work-mode-label" shrink={true} sx={labelStyles}>
                  Work Mode
                </InputLabel>
                <MultiSelect name="workMode" control={control} options={getKeyValuePairs(WORK_MODES)} disabled={isReadOnlyView} />
              </FormControl>

              {/* Job Type Dropdown */}
              <FormControl fullWidth margin="normal">
                <InputLabel id="job-type-label" shrink={true} sx={labelStyles}>
                  Job Type
                </InputLabel>
                <MultiSelect name="jobType" control={control} options={getKeyValuePairs(JOB_TYPES)} disabled={isReadOnlyView} />
              </FormControl>
            </Grid>

            <FormControl fullWidth margin="normal">
              <InputLabel id="job-type-label" shrink={true} sx={labelStyles}>
                Minimum Qualification
              </InputLabel>
              <MultiSelect
                name="qualification"
                control={control}
                options={getKeyValuePairs(courseNames?.map((el: any) => el?.split("_")[1]))}
                disabled={isReadOnlyView}
              />
            </FormControl>

            {/* Recommended Skill Set */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="skills-label" shrink={true} sx={labelStyles}>
                Recommended Skill Set
              </InputLabel>
              <MultiSelect
                disabled={isReadOnlyView || !watchFunction?.label}
                name="skills"
                control={control}
                options={getKeyValuePairs(skills)}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="skills-label" shrink={true} sx={labelStyles}>
                Tools Required
              </InputLabel>
              <MultiSelect name="tools" control={control} options={getKeyValuePairs(toolsKnown)} disabled={isReadOnlyView} />
            </FormControl>

            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfield}>
              Experience
            </InputLabel>
            <Grid container spacing={2} mt={-1}>
              {/* Minimum Experience */}
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1}>
                  <Grid item xs={8}>
                    <Controller
                      control={control}
                      name="minExp"
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CssSelectTextField fullWidth error={!!error}>
                            <Select
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                mt: 1,
                              }}
                              labelId="min-exp-label"
                              id="min-exp-select"
                              value={field?.value}
                              error={!!error}
                              onChange={field.onChange}
                              disabled={isReadOnlyView}
                              displayEmpty={true}
                              renderValue={(value) => (value === null || value === undefined ? "Minimum" : value)}
                            >
                              {Array.from({ length: 51 }).map((_, index) => {
                                return (
                                  <MenuItem key={index} value={index}>
                                    {index.toLocaleString()}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </CssSelectTextField>
                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      control={control}
                      name="minExpUnit"
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CssSelectTextField fullWidth>
                            <Select
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                mt: 1,
                              }}
                              labelId="min-exp-unit-label"
                              id="min-exp-unit-select"
                              value={field?.value}
                              onChange={field.onChange}
                              disabled={isReadOnlyView}
                              displayEmpty={true}
                              renderValue={(value) => (value ? value : "Unit")}
                            >
                              <MenuItem value="month">Months</MenuItem>
                              <MenuItem value="year">Years</MenuItem>
                            </Select>
                          </CssSelectTextField>

                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Maximum Experience */}
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1}>
                  <Grid item xs={8}>
                    <Controller
                      control={control}
                      name="maxExp"
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CssSelectTextField fullWidth error={!!error}>
                            <Select
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                mt: 1,
                              }}
                              labelId="max-exp-label"
                              id="max-exp-select"
                              value={field?.value}
                              error={!!error}
                              onChange={field.onChange}
                              disabled={isReadOnlyView}
                              displayEmpty={true}
                              renderValue={(value) => (value === null || value === undefined ? "Maximum" : value)}
                            >
                              {Array.from({ length: 51 }).map((_, index) => {
                                return (
                                  <MenuItem key={index} value={index}>
                                    {index.toLocaleString()}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </CssSelectTextField>
                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      control={control}
                      name="maxExpUnit"
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <CssSelectTextField fullWidth>
                            <Select
                              sx={{
                                borderRadius: "99px",
                                boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                mt: 1,
                              }}
                              labelId="max-exp-unit-label"
                              id="max-exp-unit-select"
                              value={field?.value}
                              onChange={field.onChange}
                              disabled={isReadOnlyView}
                              displayEmpty={true}
                              renderValue={(value) => (value ? value : "Unit")}
                            >
                              <MenuItem value="month">Months</MenuItem>
                              <MenuItem value="year">Years</MenuItem>
                            </Select>
                          </CssSelectTextField>
                          <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                            {error ? error?.message : " "}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfield}>
              {watch("type") === "Internship" ? "Stipend" : "Salary"} Range Expected Per{" "}
              {watch("type") === "Internship" ? "Month" : "Annum"}
            </InputLabel>
            <Grid container spacing={2} mt={-1}>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="minSal"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <CssSelectTextField fullWidth error={!!error}>
                        <Select
                          sx={{
                            borderRadius: "99px",
                            boxShadow: "inset 0 0 11px 2px #dfd9d9",
                            // padding: "10px 10px",
                            mt: 1,
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={field?.value ?? ""}
                          error={!!error}
                          onChange={field.onChange}
                          disabled={isReadOnlyView}
                          displayEmpty={true}
                          renderValue={(value) =>
                            value === null || value === undefined ? "Minimum" : value.toLocaleString()
                          }
                        >
                          {Array.from({ length: 51 }).map((_, index) => {
                            const minSal =
                              index === 0 ? 0 : watch("type") === "Internship" ? index * 1000 : index * 100000;
                            return (
                              <MenuItem key={minSal} value={minSal}>
                                {minSal.toLocaleString()}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </CssSelectTextField>
                      <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                        {error ? error?.message : " "}
                      </FormHelperText>
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="maxSal"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <CssSelectTextField fullWidth error={!!error}>
                        <Select
                          sx={{
                            borderRadius: "99px",
                            boxShadow: "inset 0 0 11px 2px #dfd9d9",
                            // padding: "10px 10px",
                            mt: 1,
                          }}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={field?.value ?? ""}
                          error={!!error}
                          onChange={field.onChange}
                          disabled={isReadOnlyView}
                          displayEmpty={true}
                          renderValue={(value) =>
                            value === null || value === undefined ? "Maximum" : value.toLocaleString()
                          }
                        >
                          {Array.from({ length: 51 }).map((_, index) => {
                            const minSal =
                              index === 0 ? 0 : watch("type") === "Internship" ? index * 1000 : index * 100000;
                            return (
                              <MenuItem key={minSal} value={minSal}>
                                {minSal.toLocaleString()}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </CssSelectTextField>
                      <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                        {error ? error?.message : " "}
                      </FormHelperText>
                    </>
                  )}
                />
              </Grid>
            </Grid>

            {!isReadOnlyView && (
            <Box sx={{ display: "flex", marginTop: "20px", gap: "20px" }}>
              <Button
                variant="contained"
                disabled
                sx={{
                  backgroundColor: "white",
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "12%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                onClick={handleNext}
                sx={{
                  backgroundColor: "#756DE9",
                  color: "#fff",
                  borderRadius: "100px",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: "#5a4dbf",
                    color: "#fff",
                    borderColor: "#5a4dbf",
                  },
                }}
              >
                Save and Continue
              </Button>
            </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ padding: "20px", width: "60%" }}>
            <Box sx={{ display: "flex", gap: "20px", marginTop: "16px" }}>
              <TextInputField name="title" label="Job Title/ Designation" control={control} placeholder="" disabled={isReadOnlyView} />
            </Box>

            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfield}>
              Key Result Areas (KRA&apos;s)
            </InputLabel>
            <Typography sx={{ fontWeight: "400", fontSize: "14px", color: "#777777", marginTop: "15px" }}>
              <b>Tip:</b> To create an effective job description, focus on key result areas (KRAs), clearly outlining
              the role&apos;s responsibilities. Additionally, use concise language and bullet points to make the
              information easily digestible for potential applicants.{" "}
            </Typography>

            <TextInputField name="keyResultAreas" control={control} isTextArea disabled={isReadOnlyView} />
            <TextInputField name="hiringProcess" label="Hiring Process" control={control} isTextArea disabled={isReadOnlyView} />
            <TextInputField name="numberOfPosition" label="Number of Positions" control={control} placeholder="" disabled={isReadOnlyView} />
            <Box sx={{ mb: 1.5 }}>
              <InputLabel id="tools-label" shrink={true} sx={labelStylestextfield}>
                Perks
              </InputLabel>
            </Box>
            <MultiSelect name="perks" control={control} options={getKeyValuePairs(perks)} disabled={isReadOnlyView} />

            {!isReadOnlyView && (
            <Box sx={{ display: "flex", marginTop: "20px", gap: "20px" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "12%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                onClick={handleNext}
                sx={{
                  backgroundColor: "#756DE9",
                  color: "#fff",
                  borderRadius: "100px",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: "#5a4dbf",
                    color: "#fff",
                    borderColor: "#5a4dbf",
                  },
                }}
              >
                Save and Continue
              </Button>
            </Box>
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "left", gap: "20px" }}>
            <Box sx={{ width: "70%" }}>
              <Typography
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#1F2235",
                  marginTop: "10px",
                  backgroundColor: "#FE845A",
                  padding: "7px 5px",
                  width: "15%",
                  // color: "#fff",
                  borderRadius: "2px",
                }}
              >
                GAME CHANGER
              </Typography>
              <Box sx={{ backgroundColor: "#E6E4FE", padding: "20px 10px", margin: "20px 0px", borderRadius: "10px" }}>
                <Typography style={{ fontSize: "16px", fontWeight: "700", color: "#1F2235" }}>
                  How does it help in the hiring process?
                </Typography>
                <Typography style={{ fontSize: "16px", fontWeight: "400", color: "#1F2235" }}>
                  Understanding how well a candidate&apos;s personality fits the role increases hiring accuracy
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <InputLabel shrink sx={labelStylestextfield}>
                  Find the personality that is most suited to your function
                </InputLabel>
              </Box>
              <MultiSelect
                multiple={false}
                name="personalityTypes"
                control={control}
                disabled
                options={getKeyValuePairs(functions)}
              />

              <InputLabel id="tools-label" shrink={true} sx={labelStyleJobDescription}>
                Ideal Personalities
              </InputLabel>

              {functionPersonalities?.map((el: any, id: any) => {
                return (
                  <Box
                    key={id}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "20px",
                      border: "2px solid #C4C4C44D",
                      marginTop: "20px",
                    }}
                  >
                    <Box
                      component="img"
                      src={el?.imagePath || "/images/connector-image.png"}
                      alt={el?.displayPersonalityType || "Personality"}
                      sx={{ width: 150, height: 150, objectFit: "contain", flexShrink: 0 }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src = "/images/connector-image.png";
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", padding: "10px" }}>
                      <InputLabel id="tools-label" shrink={true} sx={labelStyleJobDescription}>
                        {el?.displayPersonalityType}
                      </InputLabel>
                      <Typography style={{ fontSize: "14px", fontWeight: "400", color: "#1B1F26B8" }}>
                        {el.typeDescriptors.split("\n").map((descriptor: any, index: any) => (
                          <span key={index}>
                            {descriptor}
                            <br />
                          </span>
                        ))}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              <Typography
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#756DE9",
                  cursor: "pointer",
                  gap: 1,
                }}
                onClick={() => setViewAll(!viewAll)}
              >
                View All Personalities <ArrowForward />
              </Typography>
              {viewAll &&
                filteredPersonalities?.map((el: any, id: any) => {
                  return (
                    <Box
                      key={id}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "20px",
                        border: "2px solid #C4C4C44D",
                        marginTop: "20px",
                      }}
                    >
                      <Box
                        component="img"
                        src={el?.imagePath || "/images/connector-image.png"}
                        alt={el?.displayPersonalityType || "Personality"}
                        sx={{ width: 150, height: 150, objectFit: "contain", flexShrink: 0 }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.src = "/images/connector-image.png";
                        }}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", padding: "10px" }}>
                        <InputLabel id="tools-label" shrink={true} sx={labelStyleJobDescription}>
                          {el?.displayPersonalityType}
                        </InputLabel>
                        <Typography style={{ fontSize: "14px", fontWeight: "400", color: "#1B1F26B8" }}>
                          {el.typeDescriptors.split("\n").map((descriptor: any, index: any) => (
                            <span key={index}>
                              {descriptor}
                              <br />
                            </span>
                          ))}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

              {!isReadOnlyView && (
              <Box sx={{ display: "flex", marginTop: "20px", gap: "20px", alignItems: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "99px",
                    fontSize: "16px",
                    textTransform: "capitalize",
                    width: "12%",
                    color: "#000000",
                    "&:hover ": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  type="submit"
                  sx={{
                    backgroundColor: "#756DE9",
                    color: "#fff",
                    borderRadius: "100px",
                    padding: "10px 20px",
                    "&:hover": {
                      backgroundColor: "#5a4dbf",
                      color: "#fff",
                      borderColor: "#5a4dbf",
                    },
                  }}
                >
                  Proceed to Publish
                </Button>
              </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                width: "30%",
                backgroundColor: "#FFF2EE",
                padding: "10px",
                height: "10%",
                marginTop: "30px",
              }}
            >
              <img src="/images/client-website/lighting-vector.png" alt="Connector" width="20px" height="20px" />
              <Typography style={{ fontSize: "14px", fontWeight: "400", color: "#FF2300" }}>
                Please note that this information is just for you to know while making candidate selection. It’s not
                part of job post and not visible to candidates
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {isSeeviAdmin && isEditMode && jobIdParam && (
        <RecommendJobModal
          open={recommendJobModalOpen}
          onClose={() => setRecommendJobModalOpen(false)}
          jobId={parseInt(jobIdParam)}
          jobTitle={title || clientJobDetails?.title}
        />
      )}
    </Box>
  );
};

export default CreateJob;
